import { useState, useCallback, useEffect, useMemo, useDeferredValue, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Mic2, Calendar, Filter, X, ShoppingCart, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LoadingSpinner } from "@/components/loading-spinner";
import { AdvancedFilterContent, type AdvancedFilters } from "@/components/advanced-filter";
import { Pagination } from "@/components/pagination";
import Navbar from "@/components/navbar";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";

import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { Link } from "wouter";
import { type MarketWithShows, type PaginatedResult, type ShowWithDetails } from "@shared/schema";
import { PlanStorage } from "@/lib/plan-storage";
import { BrowseLayout } from "@/components/layouts/browse-layout";
import { MarketCard } from "@/components/market-card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { applyAllFilters, trimEntityShows, type SearchFilters, type ShowFilterConfig } from "@shared/filter-engine";
import { calculateAudienceMatch } from "@/lib/audience-matching";

// Helper function to get default advanced filters
const getDefaultAdvancedFilters = (): AdvancedFilters => ({
  artistName: "",
  venueName: "",
  location: "",
  dateFrom: "",
  dateTo: "",
  genre: [],
  careerStage: [],
  setArtist: "",
  venueType: [],
  venueLocation: "",
  targetAudience: {
    fanGender: [],
    fanEthnicity: [],
    fanAge: [],
    fanIncome: [],
    fanAttributes: []
  },
  minMatchPercentage: 0,
});

export default function BrowseMarkets() {
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedShows, setSelectedShows] = useState<ShowWithDetails[]>([]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Single source of truth for all filters
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    const careerStageParam = params.get("careerStage");
    const venueTypeParam = params.get("venueType");
    return {
      ...getDefaultAdvancedFilters(),
      artistName: params.get("artistName") || "",
      venueName: params.get("venueName") || "",
      location: params.get("location") || "",
      genre: params.get("genre") ? [params.get("genre")!] : [],
      dateFrom: params.get("dateFrom") || "",
      dateTo: params.get("dateTo") || "",
      careerStage: careerStageParam ? careerStageParam.split(',') : [],
      venueType: venueTypeParam ? venueTypeParam.split(',') : [],
      setArtist: params.get("setArtist") || "",
    };
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [itemsPerPage] = useState(20);
  const isInitialMount = useRef(true);

  // Delay turning off initial mount protection to allow all initialization to settle
  // (persona context, debounced filters, etc. all trigger after mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Debounced filters derived from advancedFilters for API calls
  const [debouncedFilters, setDebouncedFilters] = useState(() => ({
    genre: advancedFilters.genre?.[0] || "",
    location: advancedFilters.location || "",
    dateFrom: advancedFilters.dateFrom || "",
    dateTo: advancedFilters.dateTo || "",
  }));

  // Load selected shows from localStorage on mount
  useEffect(() => {
    const storedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(storedShows);
  }, [user?.id]);

  // Listen for proposal storage changes from modals
  useEffect(() => {
    const handleStorageChange = () => {
      const storedShows = PlanStorage.getSelectedShows(user?.id);
      setSelectedShows(storedShows);
    };

    window.addEventListener('proposal-storage-changed', handleStorageChange);
    return () => window.removeEventListener('proposal-storage-changed', handleStorageChange);
  }, [user?.id]);

  // Debounce filters for live search while preventing focus loss during typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters({
        genre: advancedFilters.genre?.[0] || "",
        location: advancedFilters.location || "",
        dateFrom: advancedFilters.dateFrom || "",
        dateTo: advancedFilters.dateTo || "",
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [advancedFilters.genre, advancedFilters.location, advancedFilters.dateFrom, advancedFilters.dateTo]);

  // Sync basic filters with URL parameters
  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedFilters.genre) params.set('genre', debouncedFilters.genre);
    if (debouncedFilters.location) params.set('location', debouncedFilters.location);
    if (debouncedFilters.dateFrom) params.set('dateFrom', debouncedFilters.dateFrom);
    if (debouncedFilters.dateTo) params.set('dateTo', debouncedFilters.dateTo);

    // Preserve advanced filters in URL (artist name, venue name, career stage, venue type, set artist)
    if (advancedFilters.artistName) params.set('artistName', advancedFilters.artistName);
    if (advancedFilters.venueName) params.set('venueName', advancedFilters.venueName);
    if (advancedFilters.careerStage && advancedFilters.careerStage.length > 0) {
      params.set('careerStage', advancedFilters.careerStage.join(','));
    }
    if (advancedFilters.venueType && advancedFilters.venueType.length > 0) {
      params.set('venueType', advancedFilters.venueType.join(','));
    }
    if (advancedFilters.setArtist) {
      params.set('setArtist', advancedFilters.setArtist);
    }

    // Sync page number to URL (only if > 1 to keep URLs clean)
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    } else {
      params.delete('page');
    }

    const queryString = params.toString();
    const newUrl = `/browse-markets${queryString ? `?${queryString}` : ''}`;

    // Only update URL if it's different to avoid unnecessary history entries
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [debouncedFilters, advancedFilters.artistName, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, currentPage]);

  // Reset page to 1 when filters change (but not during initial mount period)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setCurrentPage(1);
  }, [debouncedFilters, advancedFilters.artistName, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, activePersona]);

  // Track browse markets page visit for achievements
  useEffect(() => {
    if (user) {
      const trackPageVisit = async () => {
      };
      trackPageVisit();
    }
  }, [user]);

  // Apply active persona filters
  useEffect(() => {
    if (activePersona) {
      const personaFilters: AdvancedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        },
        // NOTE: targetLocations are now converted to DMA IDs for hard filtering
        // They no longer contribute to location string or percentage match
      };
      setAdvancedFilters(personaFilters);
    } else {
      // Clear audience filters when persona is removed
      const clearedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: [],
          fanEthnicity: [],
          fanAge: [],
          fanIncome: [],
          fanAttributes: [],
        },
      };
      setAdvancedFilters(clearedFilters);
    }
  }, [activePersona]);

  // Build query parameters for server-side filtering
  // Uses debouncedFilters for text inputs (genre, location, dates) to prevent rapid API calls
  // NOTE: personaFilters are NOT sent to server — demographics are fetched separately
  // and persona filtering is done entirely client-side (consistent with browse-artists behavior)
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    // Basic filters - use debouncedFilters for text inputs to prevent rapid API calls
    if (debouncedFilters.genre) params.set('genre', debouncedFilters.genre);
    if (debouncedFilters.location) params.set('location', debouncedFilters.location);
    if (debouncedFilters.dateFrom) params.set('dateFrom', debouncedFilters.dateFrom);
    if (debouncedFilters.dateTo) params.set('dateTo', debouncedFilters.dateTo);
    // Note: venueName is NOT sent to server — it's applied client-side via applyAllFilters/trimEntityShows
    // Sending it as 'name' would incorrectly filter by market name instead of venue name
    if (advancedFilters.careerStage?.length) params.set('careerStage', advancedFilters.careerStage.join(','));
    if (advancedFilters.setArtist) params.set('setArtist', advancedFilters.setArtist);

    // DMA hard filter from persona
    if (activePersona?.targetDmaIds?.length) {
      params.set('targetDmaIds', JSON.stringify(activePersona.targetDmaIds));
    }

    return params.toString();
  }, [debouncedFilters, advancedFilters.careerStage, advancedFilters.setArtist, activePersona]);

  const { data: allMarkets, isLoading: isMarketsLoading, error } = useQuery({
    queryKey: ["/api/markets-with-shows", debouncedFilters, advancedFilters.careerStage, advancedFilters.setArtist, activePersona?.targetDmaIds],
    queryFn: async () => {
      const queryString = buildQueryParams();
      const url = `/api/markets-with-shows${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch markets');
      }
      return response.json() as Promise<MarketWithShows[]>;
    },
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const markets = allMarkets || [];

  // Stage 2: Extract unique artist IDs for demographics fetch when persona is active
  const artistIdsForDemographics = useMemo(() => {
    if (!markets.length || !activePersona) return [];
    const ids = new Set<number>();
    for (const market of markets) {
      for (const show of market.shows) {
        if ((show.artist as any)?.id) ids.add((show.artist as any).id);
      }
    }
    return Array.from(ids);
  }, [markets, activePersona]);

  // Fetch demographics separately when persona is active (two-stage approach matching browse-artists)
  const { data: artistDemographics, isLoading: isDemographicsLoading } = useQuery({
    queryKey: ["/api/artists/demographics", artistIdsForDemographics],
    queryFn: async () => {
      if (artistIdsForDemographics.length === 0) return [];
      const response = await fetch('/api/artists/demographics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artistIds: artistIdsForDemographics }),
      });
      if (!response.ok) throw new Error('Failed to fetch artist demographics');
      return response.json();
    },
    enabled: artistIdsForDemographics.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Build demographics lookup map
  const demographicsMap = useMemo(() => {
    if (!artistDemographics) return new Map();
    const map = new Map();
    for (const d of artistDemographics) {
      map.set(d.id, d);
    }
    return map;
  }, [artistDemographics]);

  // Combined loading: wait for demographics when persona is active to avoid flash of unfiltered results
  const isLoading = isMarketsLoading || (artistIdsForDemographics.length > 0 && isDemographicsLoading);

  // Filter markets and their shows using centralized filter engine
  const filteredMarketsRaw = useMemo(() => {
    if (!markets) return [];

    // Convert filters to SearchFilters format - use advancedFilters as single source of truth
    const searchFilters: SearchFilters = {
      artistName: advancedFilters.artistName,
      venueName: advancedFilters.venueName,
      marketName: advancedFilters.location, // Market location filter
      genre: advancedFilters.genre && advancedFilters.genre.length > 0 ? advancedFilters.genre[0] : undefined,
      careerStage: advancedFilters.careerStage && advancedFilters.careerStage.length > 0 ? advancedFilters.careerStage : undefined,
      location: advancedFilters.location,
      dateFrom: advancedFilters.dateFrom,
      dateTo: advancedFilters.dateTo,
      venueType: advancedFilters.venueType && advancedFilters.venueType.length > 0 ? advancedFilters.venueType : undefined,
      // HARD FILTER: DMA IDs (persona target markets) - excludes shows entirely
      targetDmaIds: activePersona?.targetDmaIds && activePersona.targetDmaIds.length > 0 ? activePersona.targetDmaIds : undefined,
      personaFilters: activePersona ? {
        fanGender: activePersona.genders || [],
        fanEthnicity: activePersona.ethnicities || [],
        fanAge: activePersona.ageRanges || [],
        fanIncome: activePersona.incomeLevels || [],
        fanAttributes: activePersona.lifestyleAttributes || [],
      } : advancedFilters.targetAudience && Object.values(advancedFilters.targetAudience).some(arr => arr.length > 0) ?
        advancedFilters.targetAudience : undefined,
      minMatchPercentage: activePersona
        ? Math.max(1, activePersona.minMatchPercentage || 0)
        : advancedFilters.minMatchPercentage,
    };

    // Apply centralized filters from shared filter engine
    let filtered = applyAllFilters(markets, searchFilters);

    // Trim each market's shows to only those matching active filters, then drop markets with 0 matches
    const showConfig: ShowFilterConfig = {
      dateFrom: searchFilters.dateFrom,
      dateTo: searchFilters.dateTo,
      genre: searchFilters.genre,
      location: searchFilters.location,
      artistName: searchFilters.artistName,
      venueName: searchFilters.venueName,
      careerStage: searchFilters.careerStage,
      venueType: searchFilters.venueType,
    };
    filtered = trimEntityShows(filtered, showConfig);

    // Pre-compute match % for all shows once (O(total_shows)) instead of per-comparison in sort
    const personaTargetAudience = searchFilters.personaFilters ? {
      fanGender: searchFilters.personaFilters.fanGender || [],
      fanEthnicity: searchFilters.personaFilters.fanEthnicity || [],
      fanAge: searchFilters.personaFilters.fanAge || [],
      fanIncome: searchFilters.personaFilters.fanIncome || [],
      fanAttributes: searchFilters.personaFilters.fanAttributes || [],
    } : undefined;
    const personaMinMatch = searchFilters.minMatchPercentage || 0;
    const showMatchCache = new Map<number, number>();

    if (personaTargetAudience && demographicsMap.size > 0) {
      // Merge demographics into artist data (two-stage approach)
      filtered = filtered.map(market => ({
        ...market,
        shows: market.shows.map(show => {
          const demo = demographicsMap.get((show.artist as any)?.id);
          if (demo) {
            return { ...show, artist: { ...show.artist, ...demo } };
          }
          return show;
        }),
      }));

      // Build match cache for all shows in a single pass
      for (const market of filtered) {
        for (const show of market.shows) {
          if (!showMatchCache.has(show.id)) {
            const artist = show.artist as any;
            const hasDemo = artist?.fanGender || artist?.fanEthnicity || artist?.fanAge || artist?.fanIncome || artist?.fanAttributes;
            if (hasDemo) {
              showMatchCache.set(show.id, calculateAudienceMatch(show.artist, personaTargetAudience).percentage);
            }
          }
        }
      }

      // Filter shows using cached values, drop markets with 0 remaining
      const effectiveMin = Math.max(1, personaMinMatch || 0);
      filtered = filtered
        .map(market => ({
          ...market,
          shows: market.shows.filter(show => {
            const cached = showMatchCache.get(show.id);
            if (cached === undefined) return true; // no demographics = keep
            return cached >= effectiveMin;
          }),
        }))
        .filter(market => market.shows.length > 0);
    }

    // Pre-compute entity sort keys in a single pass (avoids recalculating per comparison)
    const entitySortKeys = new Map<number, { perfect100: number; avgMatch: number; nextShowDate: string }>();
    for (const market of filtered) {
      let perfect100 = 0;
      let totalMatch = 0;
      let earliest = '\uffff';
      for (const show of market.shows) {
        const pct = showMatchCache.get(show.id) ?? 0;
        if (pct === 100) perfect100++;
        totalMatch += pct;
        const d = (show.date as any as string).slice(0, 10);
        if (d < earliest) earliest = d;
      }
      entitySortKeys.set(market.id, {
        perfect100,
        avgMatch: market.shows.length > 0 ? totalMatch / market.shows.length : 0,
        nextShowDate: market.shows.length > 0 ? earliest : '\uffff',
      });
    }

    // Sort using pre-computed keys (O(N log N) with simple lookups per comparison)
    return filtered.sort((a, b) => {
      const keysA = entitySortKeys.get(a.id)!;
      const keysB = entitySortKeys.get(b.id)!;

      if (personaTargetAudience) {
        if (keysA.perfect100 !== keysB.perfect100) return keysB.perfect100 - keysA.perfect100;
        if (keysA.avgMatch !== keysB.avgMatch) return keysB.avgMatch - keysA.avgMatch;
      }

      // Then by number of shows (descending)
      if (a.shows.length !== b.shows.length) return b.shows.length - a.shows.length;

      // Then by population (descending)
      const aPopulation = a.population || 0;
      const bPopulation = b.population || 0;
      if (aPopulation !== bPopulation) return bPopulation - aPopulation;

      // Finally by next show date (ascending)
      return keysA.nextShowDate.localeCompare(keysB.nextShowDate);
    });
  }, [markets, advancedFilters, activePersona, demographicsMap]);

  // Defer the output so old correctly-sorted results stay visible while new ones compute
  const filteredMarkets = useDeferredValue(filteredMarketsRaw);

  // Client-side pagination
  const totalItems = filteredMarkets.length;
  const totalShows = filteredMarkets.reduce((total, market) => total + market.shows.length, 0);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMarkets = filteredMarkets.slice(startIndex, endIndex);

  const handleBasicFilterChange = useCallback((key: string, value: string) => {
    const actualValue = value === "all" ? "" : value;
    // Update advancedFilters - handle genre as array
    if (key === 'genre') {
      setAdvancedFilters(prev => ({ ...prev, genre: actualValue ? [actualValue] : [] }));
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: actualValue }));
    }
  }, []);


  const handleBasicSearch = async () => {
    // Track search activity for achievements

    // Search is now live via debouncing - this just tracks the search activity
  };

  const handleAdvancedFiltersChange = useCallback(async (filters: AdvancedFilters) => {
    // Single source of truth - just update advancedFilters
    setAdvancedFilters(filters);

    // Close advanced filters dialog
    setShowAdvancedFilters(false);
  }, []);

  const clearFilters = () => {
    // Reset all filters to defaults (single source of truth)
    setAdvancedFilters(getDefaultAdvancedFilters());
    setCurrentPage(1);

    // Explicitly clear URL parameters
    window.history.replaceState({}, "", "/browse-markets");
  };

  // Handler for unified filter bar changes - updates single source of truth
  // NOTE: Must be defined before early returns to maintain consistent hooks order
  const handleUnifiedFilterChange = useCallback((key: string, value: string | string[]) => {
    setAdvancedFilters(prev => {
      // Handle genre: convert string to string[] for consistency
      if (key === 'genre' && typeof value === 'string') {
        return { ...prev, genre: value ? [value] : [] };
      }
      return { ...prev, [key]: value };
    });
  }, []);

  // Convert advancedFilters to BrowseFilters format for components
  // Include persona data when active so MarketCard filtering is consistent
  const convertedFilters = {
    ...advancedFilters,
    // Override targetAudience with persona data when a persona is active
    targetAudience: activePersona ? {
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    } : advancedFilters.targetAudience,
    // Include minMatchPercentage from persona
    minMatchPercentage: activePersona
      ? Math.max(1, activePersona.minMatchPercentage || 0)
      : advancedFilters.minMatchPercentage,
  };

  return (
    <BrowseLayout>
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Unified Filter Bar - always mounted to prevent focus loss */}
      <UnifiedFilterBar
        currentMethod="markets"
        filters={{
          location: advancedFilters.location,
          genre: advancedFilters.genre?.[0] || '',  // Convert string[] to string for display
          dateFrom: advancedFilters.dateFrom,
          dateTo: advancedFilters.dateTo,
          artistName: advancedFilters.artistName,
          venueName: advancedFilters.venueName,
          careerStage: advancedFilters.careerStage,
          venueType: advancedFilters.venueType,
          setArtist: advancedFilters.setArtist,
        }}
        onFilterChange={handleUnifiedFilterChange}
        onClearFilters={clearFilters}
        onAdvancedFilters={() => setShowAdvancedFilters(true)}
        isAuthenticated={!!user}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">

        {/* Advanced Filters Dialog */}
        {showAdvancedFilters && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAdvancedFilters(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Advanced Filters</h2>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="rounded-full opacity-80 ring-offset-background transition-all hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-background/90 backdrop-blur-sm border shadow-lg p-1.5 hover:shadow-xl"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              <AdvancedFilterContent 
                key={JSON.stringify(advancedFilters)}
                currentFilters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                browsePage="markets"
              />
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <p className="text-red-600">Error loading markets: {error.message}</p>
          </div>
        )}

        {/* Results Summary */}
        {!isLoading && !error && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <MapPin className="h-4 w-4" />
              <span>
                {totalItems} market{totalItems !== 1 ? 's' : ''} and {totalShows} show{totalShows !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
        )}

        {/* Markets Grid */}
        {!isLoading && !error && paginatedMarkets.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-slate-400 dark:text-slate-500 mb-4">
              <MapPin className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No markets found
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              No markets with upcoming shows available at this time
            </p>
          </div>
        ) : !isLoading && !error ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedMarkets.map((market) => (
                <MarketCard
                  key={market.id}
                  market={market}
                  isAuthenticated={!!user}
                  currentFilters={convertedFilters}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        ) : null}

        {/* Unauthenticated User Invitation */}
        {!user && (
          <div className="mt-16 bg-gradient-to-r from-blue-500/5 via-green-500/5 to-blue-500/5 rounded-2xl p-8 border border-blue-500/10">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-green-600 rounded-full mb-6">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">Unlock Market-Based Strategy</h2>
              <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
                Create a <strong>free MAX Platform™ account</strong> to explore markets by DMA, analyze regional opportunities, save markets, and build comprehensive geographic campaigns.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-3">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">DMA Targeting</h4>
                  <p className="text-sm text-neutral-600">Explore designated market areas</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-3">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Market Analysis</h4>
                  <p className="text-sm text-neutral-600">Analyze regional show density and reach</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mb-3">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Regional Campaigns</h4>
                  <p className="text-sm text-neutral-600">Build multi-market sponsorship strategies</p>
                </div>
              </div>
              <div className="flex justify-center">
                <Link href="/register">
                  <Button className="btn-max-primary px-8 py-3 text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    Create Free Account
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-neutral-500 mt-4">
                Ready to explore markets? <Link href="/login" className="text-max-brand hover:underline font-medium">Sign in here</Link>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
    </BrowseLayout>
  );
}
